import {
  CampaignComparisonMetric,
  CampaignComparisonResult,
} from 'src/services/campaignComparisonApi/interfaces/campaignComparison.interface'
import {
  MetricBadge,
  MetricIcon,
  MultiMetricCell,
  MultiMetricRow,
  MultiReport,
  MultiSeries,
} from './interfaces'

/*
 * The campaign-comparison endpoint's report, in the shape this page renders.
 *
 * The endpoint has compared up to four campaigns since it was written — every
 * section of its response is already a per-campaign array — so this is a
 * rename, not a computation. The two exceptions are noted below.
 */

const ICONS: Record<CampaignComparisonMetric['icon'], MetricIcon> = {
  globe: 'sources',
  mention: 'mentions',
  smile: 'sentiment',
  thumbsDown: 'negative',
}

/*
 * Which end of a row is the good news.
 *
 * The server decides this — `buildMetric` in campaignComparison.js carries
 * `isBetterWhenHigher: false` on Negative Mentions and defaults the other three
 * to true — but it uses the flag to compute `isImprovement` and does not put it
 * in the response. The icon is the one field that survives the trip and marks
 * exactly that row: thumbsDown IS the metric you want less of.
 *
 * If the API ever states it outright, delete this and read it instead.
 */
const LOWER_IS_BETTER: CampaignComparisonMetric['icon'] = 'thumbsDown'

// What each row calls its two ends. Wording rather than data — the design names
// a sentiment score's leader "Best" and a source count's "Most" — so it is
// decided here rather than asked of the server.
const BADGES: Record<MetricIcon, { high: MetricBadge; low: MetricBadge }> = {
  mentions: { high: 'Highest', low: 'Lowest' },
  negative: { high: 'Highest', low: 'Lowest' },
  sentiment: { high: 'Best', low: 'Lowest' },
  sources: { high: 'Most', low: 'Fewest' },
}

// The units, which the API leaves to the client: it sends 65, and whether that
// is "65", "65/100" or "65%" is the metric's business.
const formatValue = (label: string, value: number) => {
  if (label === 'Sentiment Score') {
    return `${value}/100`
  }

  if (label === 'Active Sources') {
    return String(value)
  }

  return value.toLocaleString('en-US')
}

// Mark the two ends of a row. A row of one, or one whose values are all equal,
// has no ends to mark — every cell stays plain rather than the first being
// crowned by accident.
const toMetricRow = (metric: CampaignComparisonMetric): MultiMetricRow => {
  const icon = ICONS[metric.icon] ?? 'mentions'
  const higherIsBetter = metric.icon !== LOWER_IS_BETTER
  const amounts = metric.values.map((value) => value.value)
  const high = Math.max(...amounts)
  const low = Math.min(...amounts)
  const isRanked = metric.values.length > 1 && high !== low

  const cell = (amount: number): MultiMetricCell => {
    const base = { amount, value: formatValue(metric.label, amount) }

    if (!isRanked) {
      return { ...base, badge: null, tone: null }
    }

    if (amount === high) {
      return {
        ...base,
        badge: BADGES[icon].high,
        tone: higherIsBetter ? 'good' : 'bad',
      }
    }

    if (amount === low) {
      return {
        ...base,
        badge: BADGES[icon].low,
        tone: higherIsBetter ? 'bad' : 'good',
      }
    }

    return { ...base, badge: null, tone: null }
  }

  return {
    cells: metric.values.map((value) => cell(value.value)),
    higherIsBetter,
    icon,
    label: metric.label,
    words: BADGES[icon],
  }
}

// A paragraph per sentence, which is how the design sets the insights. The
// server writes one string; splitting on the sentence break is what turns it
// into the block the mock draws without asking the server to format for a
// layout it does not know about.
const toParagraphs = (insight: string) =>
  insight
    .split(/(?<=\.)\s+(?=[A-Z])/)
    .map((line) => line.trim())
    .filter(Boolean)

export const toMultiReport = (
  result: CampaignComparisonResult
): MultiReport => {
  const order = result.campaigns.map((campaign) => campaign.campaignId)

  // Every section is keyed by campaign id, but only `campaigns` fixes the
  // order the columns are drawn in. Sorting each section by it means a section
  // that comes back in another order cannot put one campaign's figures under
  // another's name.
  const inOrder = <T extends { campaignId: string }>(rows: T[]): T[] =>
    [...rows].sort(
      (a, b) => order.indexOf(a.campaignId) - order.indexOf(b.campaignId)
    )

  const series = (rows: { campaignId: string; values: number[] }[]) =>
    inOrder(rows).map(
      (row): MultiSeries => ({
        campaignId: row.campaignId,
        values: row.values,
      })
    )

  return {
    campaigns: result.campaigns.map((campaign) => ({
      id: campaign.campaignId,
      name: campaign.campaignName,
    })),

    dailyMentions: {
      days: result.dailyMentions.days,
      series: series(result.dailyMentions.series),
    },

    keywords: inOrder(result.keywords).map((table) => ({
      campaignId: table.campaignId,
      period: result.periodLabel,
      rows: table.rows.map((row) => ({
        keyword: row.keyword,
        mentions: row.mentions,
      })),
    })),

    metrics: result.keyMetrics.map(toMetricRow),
    metricsInsight: toParagraphs(result.insight),
    periodLabel: result.periodLabel,
    sentiment: inOrder(result.sentimentSplit.campaigns).map((row) => ({
      campaignId: row.campaignId,
      negative: row.negative,
      neutral: row.neutral,
      positive: row.positive,
    })),
    sentimentInsight: toParagraphs(result.sentimentSplit.insight),

    // Transposed: the API sends a row per source carrying one share per
    // campaign, and the chart wants a series per campaign carrying one share
    // per source.
    topSources: {
      series: result.campaigns.map((campaign) => ({
        campaignId: campaign.campaignId,
        values: result.topSources.map(
          (source) =>
            source.values.find(
              (value) => value.campaignId === campaign.campaignId
            )?.share ?? 0
        ),
      })),
      sources: result.topSources.map((source) => source.name),
    },
  }
}
